import {
  getAdAssetKind,
  type AdAssetPreviewAsset,
} from "src/pages/ad-studio/components/ad-asset-kind";
import { adDisplayName } from "src/pages/ad-studio/components/ad-display-name";
import {
  type ResizeFields,
  sizeAssetResizesOf,
} from "src/pages/ad-studio/resizes-of";

/** One render to request and include in a bulk video download. */
export type VideoExportTarget = {
  /** The version id, which the export mutation echoes back, so it pairs results to targets. */
  key: string;
  assetId: string;
  /** Shown in the progress list. The archive names entries from the version's own fields, so the two can differ. */
  name: string;
  adBriefAssetVersionId: string;
  /** Drives the AI-disclosure defaults for the selection. */
  brandProfileId: string | null;
};

/** A size-asset resize as the selection reads it: its own current version renders it. */
export type SelectableResizeAsset = ResizeFields & {
  version_id?: string | null;
};

/** The slice of `AdBriefAssetFields` the selection needs; structural so tests stay small. */
export type SelectableAdAsset = AdAssetPreviewAsset & {
  id: string;
  asset_name?: string | null;
  asset_type?: string | null;
  version_id?: string | null;
  ad_brief?: {
    ad_studio_project?: { brand_profile_id?: string | null } | null;
    resizes?: readonly SelectableResizeAsset[] | null;
  } | null;
};

export type VideoExportSelection = {
  targets: VideoExportTarget[];
  /**
   * Selected ads that carry a composition but have no current version to render.
   * Surfaced to the user rather than dropped silently — the count is the only
   * signal that the download won't contain everything they picked.
   */
  unexportableCount: number;
};

/**
 * Picks the renders a selection needs before it can be downloaded as video.
 *
 * Only `composition` ads qualify. A `video-file` ad already has playable bytes at
 * `asset_url` and is served by the existing image/file download path, and statics
 * are rasterized in the browser — neither goes through the export pipeline.
 *
 * Each composition ad yields its base version plus one render per resize that
 * carries its own composition, matching what the static download does with
 * resizes. A size-asset resize renders its own current version; legacy size
 * rows are not exported.
 */
export function selectVideoExportTargets(
  assets: SelectableAdAsset[],
): VideoExportSelection {
  const targets: VideoExportTarget[] = [];
  let unexportableCount = 0;

  for (const asset of assets) {
    if (getAdAssetKind(asset) !== "composition") {
      continue;
    }
    if (!asset.version_id) {
      unexportableCount += 1;
      continue;
    }
    const name = adDisplayName({ assetName: asset.asset_name });
    const brandProfileId =
      asset.ad_brief?.ad_studio_project?.brand_profile_id ?? null;

    targets.push({
      key: asset.version_id,
      assetId: asset.id,
      name,
      adBriefAssetVersionId: asset.version_id,
      brandProfileId,
    });

    for (const resize of sizeAssetResizesOf(asset)) {
      // A size asset with no saved version has nothing rendered to export.
      if (!resize.video_composition || !resize.version_id) continue;
      const sizeLabel = resize.channel_size?.label;
      targets.push({
        key: resize.version_id,
        assetId: asset.id,
        // Same "<name> - <size>" shape the archive uses for a resize's file.
        name: sizeLabel ? `${name} - ${sizeLabel}` : name,
        adBriefAssetVersionId: resize.version_id,
        brandProfileId,
      });
    }
  }

  return { targets, unexportableCount };
}

/**
 * Splits loaded assets by which download path they take. The one place the
 * composition-vs-static rule lives, so a new `AdAssetKind` lands in one edit.
 */
export function partitionAssetsForDownload<T extends AdAssetPreviewAsset>(
  assets: T[],
): { staticAssets: T[]; videoAssets: T[] } {
  const staticAssets: T[] = [];
  const videoAssets: T[] = [];
  for (const asset of assets) {
    if (getAdAssetKind(asset) === "composition") {
      videoAssets.push(asset);
    } else {
      staticAssets.push(asset);
    }
  }
  return { staticAssets, videoAssets };
}

/**
 * Splits a grid selection into the ids the static download handles and the ids
 * that need a video render. Ids the grid isn't showing stay on the static path,
 * which is where they went before videos had their own flow.
 */
export function partitionAssetIdsForDownload(
  assetIds: string[],
  assets: Array<AdAssetPreviewAsset & { id: string }>,
): { staticAssetIds: string[]; videoAssetIds: string[] } {
  const assetsById = new Map(assets.map((asset) => [asset.id, asset]));
  const staticAssetIds: string[] = [];
  const videoAssetIds: string[] = [];
  for (const id of assetIds) {
    if (getAdAssetKind(assetsById.get(id)) === "composition") {
      videoAssetIds.push(id);
    } else {
      staticAssetIds.push(id);
    }
  }
  return { staticAssetIds, videoAssetIds };
}
